'use client';

import { ColumnDef } from '@tanstack/react-table';
import { CheckoutIntents } from 'checkout-intents';

import { Badge } from 'components/ui/badge';
import { CopyId } from 'components/ui/copy-id';
import { Timestamp } from 'components/ui/timestamp';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Typography } from 'components/ui/typography';

export type CheckoutIntentLineItemStatus = 'pending' | 'ordered' | 'failed';

export type CheckoutIntentLineItem = {
  productUrl: string;
  quantity: number;
  status?: CheckoutIntentLineItemStatus;
  failureCode?: string;
};

// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type CheckoutIntentItem = {
  id: string;
  state: string;
  stateRaw: CheckoutIntents.CheckoutIntent['state'];
  createdAt: string;
  productUrl: string;
  quantity: number;
  items: CheckoutIntentLineItem[];
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerName?: string;
  buyer: CheckoutIntents.Buyer;
  cost: {
    totalCents: number;
    subtotalCents: number;
    currency: string;
    shippingCents: number;
    taxCents: number;
    discountCents: number;
    surchargeCents: number;
  };
  orderIds?: string[];
  failReason?: string;
  failureCode?: CheckoutIntents.CheckoutIntent.FailedCheckoutIntent.FailureReason['code'];
  estimatedDeliveryDate?: Date;
};

const formatMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);

// States where the offer hasn't been retrieved yet — cost figures are
// all zero and don't reflect anything real, so we show an em-dash.
const STATES_WITHOUT_COST: ReadonlySet<CheckoutIntentItem['stateRaw']> = new Set([
  'failed',
  'retrieving_offer',
]);

// Shared tooltip label styling — mono uppercase, low-alpha white over
// the dark ink panel.
const tooltipLabelClass =
  'self-center text-left text-[9px] uppercase tracking-[0.14em] text-white/45';

export const STATE_VARIANT: Record<
  CheckoutIntentItem['stateRaw'],
  'success' | 'error' | 'warn' | 'info'
> = {
  completed: 'success',
  failed: 'error',
  awaiting_confirmation: 'warn',
  placing_order: 'info',
  retrieving_offer: 'info',
};

// Single source of truth for the state pill in the table cell + the
// detail sheet header. When `failReason` is present (failed intents),
// the badge wraps in a tooltip surfacing the reason inline so the user
// doesn't have to open the sheet to find it. Expired intents get their
// own neutral "Expired" pill instead of a red Failed — semantically
// they're a timeout, not a hard failure.
export const StateBadge = ({
  stateRaw,
  label,
  failReason,
  failureCode,
  className,
}: {
  stateRaw: CheckoutIntentItem['stateRaw'];
  label: string;
  failReason?: string;
  failureCode?: CheckoutIntentItem['failureCode'];
  className?: string;
}) => {
  const isExpired = stateRaw === 'failed' && failureCode === 'checkout_intent_expired';
  const variant = isExpired ? 'neutral' : STATE_VARIANT[stateRaw];
  const displayLabel = isExpired ? 'Expired' : label;
  const badge = (
    <Badge variant={variant} className={className}>
      {displayLabel}
    </Badge>
  );
  if (!failReason) {
    return badge;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
        >
          {badge}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-xs border border-[var(--rye-ink-3)] bg-[var(--rye-ink)] px-3.5 py-2.5 text-left font-mono text-[11px] text-[var(--rye-text-light)] shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)]"
      >
        <div className={tooltipLabelClass + ' mb-1'}>Failure reason</div>
        <div className="whitespace-normal text-left">{failReason}</div>
      </TooltipContent>
    </Tooltip>
  );
};

// Cost-breakdown tooltip mirroring the Timestamp tooltip pattern: dotted
// underline trigger → dark ink panel with mono-uppercase row labels.
const SubtotalCell = ({ cost, stateRaw }: Pick<CheckoutIntentItem, 'cost' | 'stateRaw'>) => {
  if (STATES_WITHOUT_COST.has(stateRaw)) {
    return (
      <Typography variant="mono" as="span" className="text-[var(--text-muted)]">
        —
      </Typography>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Typography
          variant="mono"
          as="span"
          tabIndex={0}
          className="inline-flex cursor-help items-center border-b border-dotted border-[var(--line-strong)] pb-px text-[var(--text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]"
        >
          {formatMoney(cost.subtotalCents, cost.currency)}
        </Typography>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 border border-[var(--rye-ink-3)] bg-[var(--rye-ink)] px-3.5 py-2.5 text-left font-mono text-[11px] text-[var(--rye-text-light)] shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)]"
      >
        <span className={tooltipLabelClass}>Subtotal</span>
        <span className="text-right tabular-nums">
          {formatMoney(cost.subtotalCents, cost.currency)}
        </span>
        {cost.discountCents > 0 && (
          <>
            <span className={tooltipLabelClass}>Discount</span>
            <span className="text-right tabular-nums">
              −{formatMoney(cost.discountCents, cost.currency)}
            </span>
          </>
        )}
        <span className={tooltipLabelClass}>Shipping</span>
        <span className="text-right tabular-nums">
          {formatMoney(cost.shippingCents, cost.currency)}
        </span>
        <span className={tooltipLabelClass}>Tax</span>
        <span className="text-right tabular-nums">{formatMoney(cost.taxCents, cost.currency)}</span>
        {cost.surchargeCents > 0 && (
          <>
            <span className={tooltipLabelClass}>Surcharge</span>
            <span className="text-right tabular-nums">
              {formatMoney(cost.surchargeCents, cost.currency)}
            </span>
          </>
        )}
        <span className={tooltipLabelClass}>Total</span>
        <span className="text-right tabular-nums text-[var(--rye-lime)]">
          {formatMoney(cost.totalCents, cost.currency)}
        </span>
      </TooltipContent>
    </Tooltip>
  );
};

export const columns: ColumnDef<CheckoutIntentItem>[] = [
  {
    accessorKey: 'id',
    header: 'Id',
    // Full IDs must always be visible — the data-table's default cell
    // styling truncates with ellipsis, which destroys the value here.
    meta: { cellClassName: 'whitespace-nowrap' },
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <CopyId value={row.original.id} truncateAt={Number.POSITIVE_INFINITY} />
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created on',
    cell: ({ row }) => <Timestamp date={row.original.createdAt} />,
  },
  {
    accessorKey: 'state',
    header: 'State',
    // Fixed pill width so the column reads as a consistent column of state
    // chips regardless of label length ("Failed" vs "Awaiting Confirmation").
    // The detail-sheet usage of StateBadge stays naturally sized.
    cell: ({ row }) => (
      <StateBadge
        stateRaw={row.original.stateRaw}
        label={row.original.state}
        failReason={row.original.failReason}
        failureCode={row.original.failureCode}
        className="w-[180px] justify-center"
      />
    ),
  },
  {
    accessorKey: 'productUrl',
    header: 'Product Url',
    cell: ({ row }) => (
      <div className="space-y-1">
        {row.original.items.map((lineItem, index) => (
          <Typography variant="link" asChild key={`${lineItem.productUrl}-${index}`}>
            <a
              href={lineItem.productUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="block max-w-[28ch] truncate"
            >
              {lineItem.productUrl}
            </a>
          </Typography>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    cell: ({ row }) => (
      <div className="space-y-1">
        {row.original.items.map((lineItem, index) => (
          <div key={`${lineItem.productUrl}-${index}`}>{lineItem.quantity}</div>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'cost',
    id: 'subtotal',
    header: 'Subtotal',
    cell: ({ row }) => <SubtotalCell cost={row.original.cost} stateRaw={row.original.stateRaw} />,
  },
  {
    accessorKey: 'buyerEmail',
    header: 'Buyer Email',
  },
  {
    accessorKey: 'buyerName',
    header: 'Buyer Name',
  },
];
